import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  ConnectedPlusIcon,
  H1,
  Label,
  LabeledRadio,
  LabeledSelect,
  MagicIcon,
  Popup,
  PopupHeader,
  Textarea,
  XIcon,
} from "@commonsku/styles";
import { getImageSrc } from "../../utils";
import { isPromoStandardItem, hasNoPresetConfiguration } from "./decorationModel";
import { ArtworkPicker } from "./components/ArtworkPicker";
import { resolveArtworkUrl, type ArtworkLibrarySelectedFile } from "./utils";
import "./BulkMockupGeneratorPopup.css";

interface ItemImage {
  item_image_id: string;
  image: Record<string, unknown>;
}

interface BulkItem {
  item_id: string;
  parent_type: string;
  copied_from?: string | null;
  item_images?: ItemImage[];
  item_locations?: unknown[];
  item_decorations?: unknown[];
}

type DecorationSaveMode = "save" | "images-only";
type ExistingInstructionsMode = "use" | "override";

interface ProofOption {
  key: string;
  value: string;
}

interface BulkMockupGeneratorPopupProps {
  items: BulkItem[];
  accountId: string;
  parentId: string;
  parentType: string;
  proofOptions?: ProofOption[];
  zIndex?: number;
  overlayZIndex?: number;
  onClose: () => void;
  onGenerate: (config: BulkMockupGeneratorConfig) => void;
}

export interface BulkMockupGeneratorConfig {
  readyItems: BulkItem[];
  decoratedItems: BulkItem[];
  artworkUrl: string;
  artworkFileId: string | null;
  proofId: string;
  additionalInstructions: string;
  decorationSaveMode: DecorationSaveMode;
  existingInstructionsMode: ExistingInstructionsMode;
}

function getItemThumbnail(item: BulkItem): string | null {
  const images = item.item_images ?? [];
  if (images.length === 0) return null;
  const firstImage = images[0]?.image;
  if (!firstImage) return null;
  return getImageSrc(firstImage) || null;
}


export const BulkMockupGeneratorPopup: React.FC<BulkMockupGeneratorPopupProps> = ({
  items,
  accountId,
  parentId,
  parentType,
  proofOptions = [],
  zIndex = 1000,
  overlayZIndex = 999,
  onClose,
  onGenerate,
}) => {
  const selectProofOptions = useMemo(
    () => proofOptions.map((p) => ({ value: p.key, label: p.value })),
    [proofOptions]
  );
  const { readyItems, decoratedItems, unsupportedItems } = useMemo(() => {
    const ready: BulkItem[] = [];
    const decorated: BulkItem[] = [];
    const unsupported: BulkItem[] = [];

    for (const item of items) {
      if (!isPromoStandardItem(item)) {
        unsupported.push(item);
      } else if (hasNoPresetConfiguration(item)) {
        ready.push(item);
      } else {
        decorated.push(item);
      }
    }

    return { readyItems: ready, decoratedItems: decorated, unsupportedItems: unsupported };
  }, [items]);

  const allUnsupported = readyItems.length === 0 && decoratedItems.length === 0 && unsupportedItems.length > 0;

  const [decorationSaveMode, setDecorationSaveMode] = useState<DecorationSaveMode>("save");
  const [existingInstructionsMode, setExistingInstructionsMode] = useState<ExistingInstructionsMode>("use");
  const [artworkUrl, setArtworkUrl] = useState<string | null>(null);
  const [artworkFileId, setArtworkFileId] = useState<string | null>(null);
  const [proofId, setProofId] = useState<string>("");
  const [additionalInstructions, setAdditionalInstructions] = useState("");

  const handleSelectArtwork = useCallback((file: ArtworkLibrarySelectedFile) => {
    const url = resolveArtworkUrl(file);
    const fileId = typeof file.file_id === "string" ? file.file_id : null;
    setArtworkUrl(url);
    setArtworkFileId(fileId);
  }, []);

  const canGenerate = !!artworkUrl && (readyItems.length > 0 || decoratedItems.length > 0);

  const handleGenerate = useCallback(() => {
    if (!artworkUrl) return;
    if (readyItems.length === 0 && decoratedItems.length === 0) return;
    onGenerate({
      readyItems,
      decoratedItems,
      artworkUrl,
      artworkFileId,
      proofId,
      additionalInstructions,
      decorationSaveMode,
      existingInstructionsMode,
    });
  }, [
    readyItems,
    decoratedItems,
    artworkUrl,
    artworkFileId,
    proofId,
    additionalInstructions,
    decorationSaveMode,
    existingInstructionsMode,
    onGenerate,
  ]);

  if (allUnsupported) {
    return (
      <UnsupportedProductsModal
        zIndex={zIndex}
        overlayZIndex={overlayZIndex}
        onClose={onClose}
      />
    );
  }

  return (
    <Popup
      zIndex={zIndex}
      overlayZIndex={overlayZIndex}
      className="bulk-mockup-popup"
      contentClassName="bulk-mockup-content-wrapper"
      header={
        <PopupHeader className="bulk-mockup-header">
          <H1 className="bulk-mockup-title">
            AI Configure{" "}
            <span className="bulk-mockup-title-beta">Beta</span>
          </H1>
          <XIcon altText="Close" onClick={onClose} size="large" className="bulk-mockup-close-button" />
        </PopupHeader>
      }
    >
      <div className="bulk-mockup-body">
        <div className="bulk-mockup-left">
          {readyItems.length > 0 && (
            <ItemSection
              count={readyItems.length}
              heading={`${readyItems.length === 1 ? "has" : "have"} no decoration information`}
              items={readyItems}
            >
              <div className="bulk-mockup-radio-group">
                <LabeledRadio
                  checked={decorationSaveMode === "save"}
                  onClick={() => setDecorationSaveMode("save")}
                  label="Generate mockups and save AI decoration information"
                  name="decoration-save-mode"
                />
                <LabeledRadio
                  checked={decorationSaveMode === "images-only"}
                  onClick={() => setDecorationSaveMode("images-only")}
                  label="Generate mockups only"
                  name="decoration-save-mode"
                />
              </div>
            </ItemSection>
          )}

          {decoratedItems.length > 0 && (
            <ItemSection
              count={decoratedItems.length}
              heading={`${decoratedItems.length === 1 ? "has" : "have"} existing decoration information`}
              items={decoratedItems}
            >
              <div className="bulk-mockup-radio-group">
                <LabeledRadio
                  checked={existingInstructionsMode === "use"}
                  onClick={() => setExistingInstructionsMode("use")}
                  label="Create mockups using existing decoration information"
                  name="existing-instructions-mode"
                />
                <LabeledRadio
                  checked={existingInstructionsMode === "override"}
                  onClick={() => setExistingInstructionsMode("override")}
                  label="Replace with AI decoration information"
                  name="existing-instructions-mode"
                />
              </div>
            </ItemSection>
          )}

          {unsupportedItems.length > 0 && (
            <ItemSection
              count={unsupportedItems.length}
              heading="cannot be configured"
              items={unsupportedItems}
            >
              <p className="bulk-mockup-unsupported-note">
                AI config is only available for{" "}
                <ConnectedPlusIcon
                  size="small"
                  className="bulk-mockup-connected-icon"
                />
                <strong>Connected+</strong> products
              </p>
            </ItemSection>
          )}
        </div>

        <div className="bulk-mockup-right">
          <div className="bulk-mockup-artwork-container">
            <Label>Artwork</Label>
            <ArtworkPicker
              parent_type={parentType}
              parent_id={parentId}
              account_id={accountId}
              onSelect={handleSelectArtwork}
              value={artworkUrl ?? undefined}
            />
          </div>

          <LabeledSelect
            label="Proof Required"
            name="proof-required"
            value={selectProofOptions.find((o) => o.value === proofId) ?? null}
            options={selectProofOptions}
            inPopup={true}
            onChange={(option: { value: string } | null) => {
              setProofId(option?.value ?? "");
            }}
          />

          <div className="bulk-mockup-textarea-container">
            <Label>Additional Instructions</Label>
            <Textarea
              value={additionalInstructions}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setAdditionalInstructions(e.target.value)
              }
              placeholder="Adjust placement or size for best fit on the product."
            />
          </div>
        </div>
      </div>

      <div className="bulk-mockup-actions">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canGenerate} onClick={handleGenerate} className="bulk-mockup-generate-btn">
          <MagicIcon color="white" size="small" />
          Configure
        </Button>
      </div>
    </Popup>
  );
};

interface UnsupportedProductsModalProps {
  zIndex: number;
  overlayZIndex: number;
  onClose: () => void;
}

const UnsupportedProductsModal: React.FC<UnsupportedProductsModalProps> = ({
  zIndex,
  overlayZIndex,
  onClose,
}) => (
  <Popup
    zIndex={zIndex}
    overlayZIndex={overlayZIndex}
    className="bulk-mockup-unsupported-popup"
    header={
      <PopupHeader className="bulk-mockup-header">
        <H1 className="bulk-mockup-title">Products Not Supported</H1>
        <XIcon altText="Close" onClick={onClose} size="large" className="bulk-mockup-close-button" />
      </PopupHeader>
    }
  >
    <p className="bulk-mockup-unsupported-body">
      Our AI generator only works with Connected+ products. You will have to
      manually edit these products.
    </p>
    <div className="bulk-mockup-unsupported-actions">
      <Button onClick={onClose}>Close</Button>
    </div>
  </Popup>
);

interface ItemSectionProps {
  count: number;
  heading: string;
  items: BulkItem[];
  children: React.ReactNode;
}

const ItemSection: React.FC<ItemSectionProps> = ({ count, heading, items, children }) => (
  <div className="bulk-mockup-section">
    <p className="bulk-mockup-section-heading">
      <strong className="bulk-mockup-section-count">{count} Product{count !== 1 ? "s" : ""}</strong>{" "}
      {heading}
    </p>
    {children}
    <div className="bulk-mockup-thumbnails">
      {items.map((item) => {
        const src = getItemThumbnail(item);
        return (
          <div key={item.item_id} className="bulk-mockup-thumb">
            {src ? (
              <img src={src} alt="Product" />
            ) : (
              <div className="bulk-mockup-thumb-placeholder" />
            )}
          </div>
        );
      })}
    </div>
  </div>
);
